import React from 'react';

const CardPreview = ({ config }) => {
  const { mode = 'card', cardStyle = {}, viewTree = [] } = config;

  const getBackgroundStyle = () => {
    if (cardStyle.backgroundType === 'solid') {
      return { backgroundColor: cardStyle.backgroundColor || '#ffffff' };
    } else if (cardStyle.backgroundType === 'gradient') {
      const colors = cardStyle.gradientColors || ['#F26F29', '#F03224'];
      return {
        background: `linear-gradient(135deg, ${colors[0]}, ${colors[1]})`,
      };
    }
    return { backgroundColor: '#ffffff' };
  };

  const getFontWeight = (weight) => {
    switch (weight) {
      case 'medium':
        return '500';
      case 'semibold':
        return '600';
      case 'bold':
        return '700';
      default:
        return '400';
    }
  };

  const getAlignmentStyle = (alignment, type) => {
    if (type === 'HStack') {
      switch (alignment) {
        case 'leading':
          return { justifyContent: 'flex-start' };
        case 'center':
          return { justifyContent: 'center' };
        case 'trailing':
          return { justifyContent: 'flex-end' };
        default:
          return { justifyContent: 'center' };
      }
    } else if (type === 'VStack') {
      switch (alignment) {
        case 'top':
          return { alignItems: 'flex-start' };
        case 'center':
          return { alignItems: 'center' };
        case 'bottom':
          return { alignItems: 'flex-end' };
        default:
          return { alignItems: 'center' };
      }
    }
    return {};
  };

  // Helper function to apply modifiers to style
  const applyModifiers = (baseStyle, modifiers) => {
    if (!modifiers) return baseStyle;
    
    const style = { ...baseStyle };

    // Padding
    if (modifiers.padding) {
      const p = modifiers.padding;
      if (p.all !== null && p.all !== undefined) {
        style.padding = `${p.all}px`;
      } else {
        if (p.horizontal !== null && p.horizontal !== undefined) {
          style.paddingLeft = `${p.horizontal}px`;
          style.paddingRight = `${p.horizontal}px`;
        }
        if (p.vertical !== null && p.vertical !== undefined) {
          style.paddingTop = `${p.vertical}px`;
          style.paddingBottom = `${p.vertical}px`;
        }
        if (p.top !== null && p.top !== undefined) style.paddingTop = `${p.top}px`;
        if (p.bottom !== null && p.bottom !== undefined) style.paddingBottom = `${p.bottom}px`;
        if (p.leading !== null && p.leading !== undefined) style.paddingLeft = `${p.leading}px`;
        if (p.trailing !== null && p.trailing !== undefined) style.paddingRight = `${p.trailing}px`;
      }
    }

    // Frame
    if (modifiers.frame) {
      const f = modifiers.frame;
      if (f.width !== null && f.width !== undefined) style.width = `${f.width}px`;
      if (f.height !== null && f.height !== undefined) style.height = `${f.height}px`;
      if (f.minWidth !== null && f.minWidth !== undefined) style.minWidth = `${f.minWidth}px`;
      if (f.maxWidth !== null && f.maxWidth !== undefined && !f.maxWidthInfinity) {
        style.maxWidth = `${f.maxWidth}px`;
      }
      if (f.minHeight !== null && f.minHeight !== undefined) style.minHeight = `${f.minHeight}px`;
      if (f.maxHeight !== null && f.maxHeight !== undefined && !f.maxHeightInfinity) {
        style.maxHeight = `${f.maxHeight}px`;
      }
      if (f.maxWidthInfinity) style.width = '100%';
      if (f.maxHeightInfinity) style.height = '100%';
    }

    // Background
    if (modifiers.background && modifiers.background.type !== 'none') {
      if (modifiers.background.type === 'color') {
        style.backgroundColor = modifiers.background.color || '#FFFFFF';
      } else if (modifiers.background.type === 'gradient') {
        const colors = modifiers.background.gradientColors || ['#F26F29', '#F03224'];
        style.background = `linear-gradient(135deg, ${colors[0]}, ${colors[1]})`;
      }
    }

    // Foreground Color (for text elements)
    if (modifiers.foregroundColor) {
      style.color = modifiers.foregroundColor;
    }

    // Opacity
    if (modifiers.opacity !== null && modifiers.opacity !== undefined && modifiers.opacity !== 1.0) {
      style.opacity = modifiers.opacity;
    }

    // Corner Radius
    if (modifiers.cornerRadius && modifiers.cornerRadius > 0) {
      style.borderRadius = `${modifiers.cornerRadius}px`;
    }

    // Shadow
    if (modifiers.shadow && modifiers.shadow.enabled) {
      const s = modifiers.shadow;
      style.boxShadow = `${s.x || 0}px ${s.y || 4}px ${s.radius || 8}px rgba(0, 0, 0, ${s.opacity || 0.2})`;
    }

    // Border
    if (modifiers.border && modifiers.border.enabled) {
      style.border = `${modifiers.border.width || 1}px solid ${modifiers.border.color || '#CCCCCC'}`;
    }

    // Rotation
    if (modifiers.rotation && modifiers.rotation !== 0) {
      const currentTransform = style.transform || '';
      style.transform = `${currentTransform} rotate(${modifiers.rotation}deg)`.trim();
    }

    // Scale
    if (modifiers.scale) {
      const sx = modifiers.scale.x || 1.0;
      const sy = modifiers.scale.y || 1.0;
      if (sx !== 1.0 || sy !== 1.0) {
        const currentTransform = style.transform || '';
        style.transform = `${currentTransform} scale(${sx}, ${sy})`.trim();
      }
    }

    // Offset
    if (modifiers.offset) {
      const ox = modifiers.offset.x || 0;
      const oy = modifiers.offset.y || 0;
      if (ox !== 0 || oy !== 0) {
        const currentTransform = style.transform || '';
        style.transform = `${currentTransform} translate(${ox}px, ${oy}px)`.trim();
      }
    }

    // Clip Shape
    if (modifiers.clipShape && modifiers.clipShape !== 'none') {
      style.overflow = 'hidden';
      switch (modifiers.clipShape) {
        case 'circle':
          style.borderRadius = '50%';
          break;
        case 'roundedRectangle':
          const radius = modifiers.cornerRadius || 8;
          style.borderRadius = `${radius}px`;
          break;
        case 'capsule':
          style.borderRadius = '9999px';
          break;
        case 'ellipse':
          style.borderRadius = '50%';
          break;
        default:
          break;
      }
    }

    // Aspect Ratio
    if (modifiers.aspectRatio && modifiers.aspectRatio.enabled) {
      const ratio = modifiers.aspectRatio.ratio || 1.0;
      style.aspectRatio = `${ratio} / 1`;
      style.width = style.width || '100%';
    }

    return style;
  };

  const renderViewNode = (node) => {
    if (!node) return null;

    const { type, properties = {}, children = [] } = node;

    switch (type) {
      case 'HStack':
        return (
          <div
            style={applyModifiers({
              display: 'flex',
              flexDirection: 'row',
              gap: `${properties.spacing || 8}px`,
              alignItems: 'center',
              ...getAlignmentStyle(properties.alignment, 'HStack'),
            }, properties.modifiers)}
          >
            {children.map((child, index) => (
              <React.Fragment key={child.id || index}>
                {renderViewNode(child)}
              </React.Fragment>
            ))}
          </div>
        );

      case 'VStack':
        return (
          <div
            style={applyModifiers({
              display: 'flex',
              flexDirection: 'column',
              gap: `${properties.spacing || 8}px`,
              ...getAlignmentStyle(properties.alignment, 'VStack'),
            }, properties.modifiers)}
          >
            {children.map((child, index) => (
              <React.Fragment key={child.id || index}>
                {renderViewNode(child)}
              </React.Fragment>
            ))}
          </div>
        );

      case 'ZStack':
        return (
          <div
            style={applyModifiers({
              position: 'relative',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }, properties.modifiers)}
          >
            {children.map((child, index) => (
              <div
                key={child.id || index}
                style={{
                  position: index === 0 ? 'relative' : 'absolute',
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                }}
              >
                {renderViewNode(child)}
              </div>
            ))}
          </div>
        );

      case 'Text':
        return (
          <div
            style={applyModifiers({
              fontSize: `${properties.fontSize || 16}px`,
              fontWeight: getFontWeight(properties.fontWeight),
              color: properties.color || '#000000',
              textAlign: properties.alignment === 'leading' ? 'left' : properties.alignment === 'trailing' ? 'right' : 'center',
            }, properties.modifiers)}
          >
            {properties.content || 'Text'}
          </div>
        );

      case 'Button':
        return (
          <button
            style={applyModifiers({
              padding: '8px 16px',
              borderRadius: '8px',
              border: properties.style === 'plain' ? 'none' : '1px solid #ccc',
              backgroundColor: properties.style === 'borderedProminent' ? '#007AFF' : 'transparent',
              color: properties.style === 'borderedProminent' ? '#fff' : '#007AFF',
              cursor: 'pointer',
              fontSize: '14px',
            }, properties.modifiers)}
            onClick={(e) => e.preventDefault()}
          >
            {properties.title || 'Button'}
          </button>
        );

      case 'Image':
        return (
          <div
            style={applyModifiers({
              width: `${properties.size || 24}px`,
              height: `${properties.size || 24}px`,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              backgroundColor: '#f0f0f0',
              borderRadius: '4px',
            }, properties.modifiers)}
          >
            <span style={{ fontSize: `${(properties.size || 24) * 0.6}px` }}>
              {properties.source ? '🖼️' : '📷'}
            </span>
          </div>
        );

      case 'Spacer':
        return (
          <div
            style={applyModifiers({
              flex: 1,
              minHeight: properties.minLength ? `${properties.minLength}px` : 'auto',
              minWidth: properties.minLength ? `${properties.minLength}px` : 'auto',
            }, properties.modifiers)}
          />
        );

      case 'Divider':
        return (
          <div
            style={applyModifiers({
              width: '100%',
              height: `${properties.thickness || 1}px`,
              backgroundColor: properties.color || '#CCCCCC',
            }, properties.modifiers)}
          />
        );

      case 'List':
        return (
          <div
            style={applyModifiers({
              display: 'flex',
              flexDirection: 'column',
              gap: '4px',
            }, properties.modifiers)}
          >
            {children.map((child, index) => (
              <div
                key={child.id || index}
                style={{
                  padding: '8px',
                  borderBottom: '1px solid #e0e0e0',
                }}
              >
                {renderViewNode(child)}
              </div>
            ))}
          </div>
        );

      case 'ScrollView':
        return (
          <div
            style={applyModifiers({
              maxHeight: '200px',
              overflowY: 'auto',
              display: 'flex',
              flexDirection: 'column',
              gap: '8px',
            }, properties.modifiers)}
          >
            {children.map((child, index) => (
              <React.Fragment key={child.id || index}>
                {renderViewNode(child)}
              </React.Fragment>
            ))}
          </div>
        );

      default:
        return (
          <div style={applyModifiers({ color: '#999' }, properties.modifiers)}>
            Unknown view type: {type}
          </div>
        );
    }
  };

  const getCardStyle = () => {
    return {
      padding: `${cardStyle.padding || 16}px`,
      borderRadius: `${cardStyle.cornerRadius || 12}px`,
      boxShadow: `0 ${cardStyle.shadowRadius || 8}px ${(cardStyle.shadowRadius || 8) * 2}px rgba(0, 0, 0, ${cardStyle.shadowOpacity || 0.2})`,
      ...getBackgroundStyle(),
      minWidth: '200px',
      maxWidth: '400px',
    };
  };

  // Full UI mode - render full screen without card container
  if (mode === 'fullUI') {
    return (
      <div
        className="w-full h-full bg-gray-900"
        style={{ minWidth: 0, minHeight: 0, boxSizing: 'border-box', display: 'flex', flexDirection: 'column' }}
      >
        {viewTree.length === 0 ? (
          <div style={{ color: '#999', textAlign: 'center', padding: '20px', flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            Add views to see preview
          </div>
        ) : (
          <div style={{ flex: 1, padding: '16px', overflow: 'auto' }}>
            {viewTree.map((node, index) => (
              <React.Fragment key={node.id || index}>
                {renderViewNode(node)}
              </React.Fragment>
            ))}
          </div>
        )}
      </div>
    );
  }

  // Card mode - render with card container (existing behavior)
  return (
    <div
      className="w-full h-full flex items-center justify-center bg-gray-900 p-4"
      style={{ minWidth: 0, minHeight: 0, boxSizing: 'border-box' }}
    >
      <div style={getCardStyle()}>
        {viewTree.length === 0 ? (
          <div style={{ color: '#999', textAlign: 'center', padding: '20px' }}>
            Add views to see preview
          </div>
        ) : (
          <div>
            {viewTree.map((node, index) => (
              <React.Fragment key={node.id || index}>
                {renderViewNode(node)}
              </React.Fragment>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default CardPreview;
